import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";

const API_BASE_URL =
  (process.env.REACT_APP_API_URL || "https://tools.microters.com").replace(/\/$/, "");

const COLORS = {
  background: "#fff9f7",
  navy: "#1e2b49",
  orange: "#ff5a36",
  muted: "#68758c",
  border: "#dce3ec",
};

const TONES = [
  "Professional",
  "Formal",
  "Friendly",
  "Conversational",
  "Persuasive",
  "Creative",
  "Informative",
  "Confident",
];

const LANGUAGES = [
  "English",
  "Bengali",
  "Spanish",
  "French",
  "German",
  "Italian",
  "Portuguese",
  "Hindi",
  "Arabic",
  "Chinese",
  "Japanese",
  "Korean",
];

const countWords = (value) => value.trim().match(/[^\s]+/g)?.length || 0;

const getErrorMessage = (error) =>
  error.response?.data?.message ||
  error.response?.data?.errors?.[0] ||
  (error.code === "ECONNABORTED"
    ? "The request timed out. Please try again."
    : error.message || "Could not generate sentences.");

const copyText = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
};

export default function AISentenceGenerator() {
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("");
  const [language, setLanguage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [result, setResult] = useState(null);

  const topicWordCount = useMemo(() => countWords(topic), [topic]);

  const clearMessages = () => {
    if (error) setError("");
    if (notice) setNotice("");
  };

  const handleGenerate = async (event) => {
    event.preventDefault();
    const cleanTopic = topic.trim();

    if (!cleanTopic) {
      setError("Please enter a topic or keyword.");
      return;
    }

    if (!tone) {
      setError("Please select a tone.");
      return;
    }

    if (!language) {
      setError("Please select a language.");
      return;
    }

    setLoading(true);
    setError("");
    setNotice("");
    setResult(null);

    console.log("[SENTENCE GENERATOR UI] Sending request:", {
      endpoint: `${API_BASE_URL}/api/sentence-generator`,
      topicCharacters: cleanTopic.length,
      tone,
      language,
    });

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/sentence-generator`,
        { topic: cleanTopic, tone, language },
        { timeout: 70000 }
      );

      const responseData = response.data?.data;

      if (
        !response.data?.success ||
        !Array.isArray(responseData?.sentences) ||
        responseData.sentences.length === 0
      ) {
        throw new Error("The server returned no usable sentences.");
      }

      setResult({
        ...responseData,
        sentences: responseData.sentences.filter(Boolean),
      });
      setNotice("Sentences generated successfully.");

      console.log("[SENTENCE GENERATOR UI] Result received:", {
        sentences: responseData.sentences.length,
        totalWords: responseData.totalWordCount,
        totalCharacters: responseData.totalCharacterCount,
      });
    } catch (requestError) {
      const message = getErrorMessage(requestError);
      setError(message);

      console.error("[SENTENCE GENERATOR UI] Request failed:", {
        message,
        status: requestError.response?.status,
        response: requestError.response?.data,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text, label = "Sentence") => {
    try {
      await copyText(text);
      setError("");
      setNotice(`${label} copied to the clipboard.`);
    } catch (copyError) {
      console.error("[SENTENCE GENERATOR UI] Copy failed:", copyError);
      setError("Could not copy the sentence.");
    }
  };

  const handleCopyAll = async () => {
    if (!result?.sentences?.length) return;
    const text = result.sentences.map((sentence, index) => `${index + 1}. ${sentence}`).join("\n\n");
    await handleCopy(text, "All sentences");
  };

  const handleDownload = () => {
    if (!result?.sentences?.length) return;

    const text = `Topic / Keyword:\n${topic.trim()}\n\nGenerated Sentences:\n\n${result.sentences
      .map((sentence, index) => `${index + 1}. ${sentence}`)
      .join("\n\n")}`;
    const file = new Blob([text], { type: "text/plain;charset=utf-8" });
    const fileUrl = URL.createObjectURL(file);
    const anchor = document.createElement("a");
    anchor.href = fileUrl;
    anchor.download = "ai-generated-sentences.txt";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(fileUrl);
    setError("");
    setNotice("Sentences downloaded as a TXT file.");
  };

  const handleReset = () => {
    setTopic("");
    setTone("");
    setLanguage("");
    setLoading(false);
    setError("");
    setNotice("");
    setResult(null);
  };

  return (
    <main
      className="min-vh-100 py-5"
      style={{ backgroundColor: COLORS.background, color: COLORS.navy }}
    >
      <Container style={{ maxWidth: "1120px" }}>
      

        <Card className="border rounded-4 shadow-sm" style={{ borderColor: COLORS.border }}>
          <Card.Body className="p-4 p-md-5">
            <Form onSubmit={handleGenerate} noValidate>
              <Form.Group className="mb-4" controlId="sentenceTopic">
                <Form.Label className="fw-semibold">
                  Topic / Keyword <span style={{ color: COLORS.orange }}>*</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  maxLength={500}
                  value={topic}
                  disabled={loading}
                  placeholder="e.g. Digital Marketing Strategies..."
                  onChange={(event) => {
                    setTopic(event.target.value);
                    clearMessages();
                  }}
                  style={{ minHeight: "130px", resize: "vertical" }}
                />
                <div
                  className="d-flex justify-content-between small mt-2"
                  style={{ color: COLORS.muted }}
                >
                  <span>{topicWordCount.toLocaleString()} words</span>
                  <span>{topic.length.toLocaleString()}/500 characters</span>
                </div>
              </Form.Group>

              <Row className="g-3 mb-4">
                <Col md={6}>
                  <Form.Group controlId="sentenceTone">
                    <Form.Label className="fw-semibold">
                      Tone <span style={{ color: COLORS.orange }}>*</span>
                    </Form.Label>
                    <Form.Select
                      value={tone}
                      disabled={loading}
                      onChange={(event) => {
                        setTone(event.target.value);
                        clearMessages();
                      }}
                    >
                      <option value="">Select tone...</option>
                      {TONES.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="sentenceLanguage">
                    <Form.Label className="fw-semibold">
                      Language <span style={{ color: COLORS.orange }}>*</span>
                    </Form.Label>
                    <Form.Select
                      value={language}
                      disabled={loading}
                      onChange={(event) => {
                        setLanguage(event.target.value);
                        clearMessages();
                      }}
                    >
                      <option value="">Select language...</option>
                      {LANGUAGES.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {error && (
                <Alert variant="danger" className="py-2" role="alert">
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                size="lg"
                disabled={loading}
                className="w-100 border-0 rounded-pill fw-bold py-3 shadow-sm"
                style={{ backgroundColor: COLORS.orange }}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Generating Sentences...
                  </>
                ) : (
                  <>✦&nbsp; Generate Sentences</>
                )}
              </Button>
            </Form>
          </Card.Body>
        </Card>

        {result && (
          <Card className="border-0 rounded-4 shadow-sm mt-4 overflow-hidden">
            <Card.Header className="bg-white border-bottom p-4">
              <div className="d-flex flex-column flex-lg-row gap-3 justify-content-between align-items-lg-center">
                <div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-success fs-5" aria-hidden="true">✓</span>
                    <h2 className="h4 fw-bold mb-0">Generated Sentences</h2>
                  </div>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    <Badge bg="light" text="dark" className="border fw-normal">
                      {(result.sentenceCount ?? result.sentences.length).toLocaleString()} sentences
                    </Badge>
                    <Badge bg="light" text="dark" className="border fw-normal">
                      {(result.totalWordCount ?? result.sentences.reduce((sum, item) => sum + countWords(item), 0)).toLocaleString()} words
                    </Badge>
                    <Badge bg="light" text="dark" className="border fw-normal">
                      {result.tone || tone}
                    </Badge>
                    <Badge bg="light" text="dark" className="border fw-normal">
                      {result.language || language}
                    </Badge>
                  </div>
                </div>

                <div className="d-flex flex-wrap gap-2">
                  <Button variant="outline-secondary" onClick={handleCopyAll}>
                    Copy All
                  </Button>
                  <Button variant="outline-secondary" onClick={handleDownload}>
                    Download TXT
                  </Button>
                  <Button variant="light" className="border" onClick={handleReset}>
                    Start Over
                  </Button>
                </div>
              </div>
            </Card.Header>

            <Card.Body className="p-4 p-md-5">
              {notice && (
                <Alert variant="success" className="py-2" role="status">
                  {notice}
                </Alert>
              )}

              <div className="d-grid gap-3">
                {result.sentences.map((sentence, index) => (
                  <div key={`${sentence.slice(0, 30)}-${index}`} className="p-4 rounded-3 border bg-light">
                    <div className="d-flex gap-3 justify-content-between align-items-start">
                      <div className="d-flex gap-3">
                        <Badge pill bg="secondary" className="mt-1">
                          {index + 1}
                        </Badge>
                        <p className="fs-5 mb-0" style={{ color: "#34425b", lineHeight: 1.7 }}>
                          {sentence}
                        </p>
                      </div>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handleCopy(sentence, `Sentence ${index + 1}`)}
                      >
                        Copy
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        )}
      </Container>
    </main>
  );
}
